import React, { useState, useEffect, useCallback } from 'react';
import { Trash2, TrendingDown, TrendingUp, Minus } from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, ReferenceLine } from 'recharts';
import { supabase } from './supabase';

const LBS_PER_KG = 2.20462;

const getLocalDateString = (date = new Date()) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const WeightTracker = ({ session, refreshKey, unit, goalWeight, setGoalWeight }) => {
  // Weight data
  const [weightEntries, setWeightEntries] = useState([]); // [{ id, date, weightLbs }] sorted by date asc

  // Form inputs
  const [weightInput, setWeightInput] = useState('');
  const [dateInput, setDateInput] = useState(getLocalDateString());

  // Chart
  const [chartPeriod, setChartPeriod] = useState('3m');

  // UI
  const [isLoading, setIsLoading] = useState(true);
  const [message, setMessage] = useState(null);

  // Conversion helpers
  const toDisplay = useCallback((lbs) => {
    if (lbs == null) return null;
    const val = unit === 'kg' ? lbs / LBS_PER_KG : lbs;
    return Math.round(val * 10) / 10;
  }, [unit]);

  const toLbs = useCallback((value) => {
    if (value == null) return null;
    return unit === 'kg' ? value * LBS_PER_KG : value;
  }, [unit]);

  // Load data
  const loadData = useCallback(async () => {
    try {
      const [entriesResult, goalResult] = await Promise.all([
        supabase
          .from('weight_entries')
          .select('*')
          .eq('user_id', session.user.id)
          .order('date', { ascending: true }),
        supabase
          .from('weight_goals')
          .select('*')
          .eq('user_id', session.user.id)
          .single()
      ]);

      if (entriesResult.data) {
        setWeightEntries(entriesResult.data.map(e => ({
          id: e.id,
          date: e.date,
          weightLbs: parseFloat(e.weight_lbs)
        })));
      }

      if (goalResult.data) {
        setGoalWeight(parseFloat(goalResult.data.target_weight_lbs));
      }
    } catch (err) {
      console.error('Error loading weight data:', err);
    }

    setIsLoading(false);
  }, [session?.user?.id, setGoalWeight]);

  useEffect(() => {
    if (session?.user) {
      loadData();
    } else {
      setIsLoading(false);
    }
  }, [session, loadData, refreshKey]);

  // Log weight
  const logWeight = async () => {
    const weight = parseFloat(weightInput);
    if (isNaN(weight) || weight <= 0) {
      setMessage({ type: 'error', text: 'Please enter a valid weight' });
      return;
    }

    const weightLbs = toLbs(weight);
    const date = dateInput || getLocalDateString();

    if (session?.user) {
      const { data, error } = await supabase
        .from('weight_entries')
        .upsert({
          user_id: session.user.id,
          date,
          weight_lbs: weightLbs
        }, { onConflict: 'user_id,date' })
        .select()
        .single();

      if (error) {
        setMessage({ type: 'error', text: 'Failed to save weight entry' });
        return;
      }

      setWeightEntries(prev => {
        const filtered = prev.filter(e => e.date !== date);
        const newEntry = { id: data.id, date, weightLbs: parseFloat(data.weight_lbs) };
        return [...filtered, newEntry].sort((a, b) => a.date.localeCompare(b.date));
      });
    } else {
      // Anonymous - store in memory
      const tempId = `temp-${Date.now()}`;
      setWeightEntries(prev => {
        const filtered = prev.filter(e => e.date !== date);
        return [...filtered, { id: tempId, date, weightLbs }].sort((a, b) => a.date.localeCompare(b.date));
      });
    }

    setWeightInput('');
    setMessage({ type: 'success', text: `Weight logged: ${weight} ${unit}` });
    setTimeout(() => setMessage(null), 3000);
  };

  // Delete entry
  const deleteEntry = async (id) => {
    if (session?.user && !String(id).startsWith('temp-')) {
      const { error } = await supabase
        .from('weight_entries')
        .delete()
        .eq('id', id);

      if (error) {
        setMessage({ type: 'error', text: 'Failed to delete entry' });
        return;
      }
    }

    setWeightEntries(prev => prev.filter(e => e.id !== id));
  };

  // Computed values
  const currentWeight = weightEntries.length > 0 ? weightEntries[weightEntries.length - 1] : null;
  const previousWeight = weightEntries.length > 1 ? weightEntries[weightEntries.length - 2] : null;

  const trend = (() => {
    if (!currentWeight || !previousWeight) return null;
    const diff = currentWeight.weightLbs - previousWeight.weightLbs;
    if (Math.abs(diff) < 0.1) return 'same';
    return diff > 0 ? 'up' : 'down';
  })();

  // Goal-aware trend coloring
  const getTrendColor = () => {
    if (!trend || trend === 'same') return 'text-gray-400';
    if (goalWeight) {
      const isLosingGoal = goalWeight < currentWeight.weightLbs;
      if (isLosingGoal) {
        return trend === 'down' ? 'text-green-500' : 'text-red-500';
      } else {
        return trend === 'up' ? 'text-green-500' : 'text-red-500';
      }
    }
    return trend === 'down' ? 'text-green-500' : 'text-red-500';
  };

  const progressToGoal = (() => {
    if (!goalWeight || weightEntries.length === 0) return null;
    const startWeight = weightEntries[0].weightLbs;
    const current = currentWeight.weightLbs;
    const goal = goalWeight;

    if (startWeight === goal) return 100;

    const totalChange = Math.abs(startWeight - goal);
    const currentChange = startWeight > goal
      ? Math.max(0, startWeight - current)
      : Math.max(0, current - startWeight);

    return Math.min(100, Math.round((currentChange / totalChange) * 100));
  })();

  // Chart data
  const getChartData = () => {
    const now = new Date();
    let cutoffDate;

    switch (chartPeriod) {
      case '1w': cutoffDate = new Date(now); cutoffDate.setDate(cutoffDate.getDate() - 7); break;
      case '1m': cutoffDate = new Date(now); cutoffDate.setMonth(cutoffDate.getMonth() - 1); break;
      case '3m': cutoffDate = new Date(now); cutoffDate.setMonth(cutoffDate.getMonth() - 3); break;
      case '6m': cutoffDate = new Date(now); cutoffDate.setMonth(cutoffDate.getMonth() - 6); break;
      case '1y': cutoffDate = new Date(now); cutoffDate.setFullYear(cutoffDate.getFullYear() - 1); break;
      default: cutoffDate = null;
    }

    const cutoff = cutoffDate ? getLocalDateString(cutoffDate) : null;

    return weightEntries
      .filter(e => !cutoff || e.date >= cutoff)
      .map(e => ({
        date: e.date,
        weight: toDisplay(e.weightLbs),
        label: new Date(e.date + 'T12:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
      }));
  };

  const chartData = getChartData();

  // Compute year boundary labels for chart separators
  const getYearBoundaries = (data) => {
    const boundaries = [];
    for (let i = 1; i < data.length; i++) {
      const prevYear = data[i - 1].date.split('-')[0];
      const currYear = data[i].date.split('-')[0];
      if (currYear !== prevYear) {
        boundaries.push({ label: data[i].label, year: currYear });
      }
    }
    return boundaries;
  };

  const showDots = chartPeriod === '1w' || chartPeriod === '1m';
  const showYearLines = ['6m', '1y', 'all'].includes(chartPeriod);

  // Format date for display
  const formatDisplayDate = (dateStr) => {
    const [y, m, d] = dateStr.split('-').map(Number);
    const date = new Date(y, m - 1, d);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="text-purple-600 text-lg">Loading...</div>
      </div>
    );
  }

  const weightDiff = currentWeight && goalWeight
    ? toDisplay(currentWeight.weightLbs - goalWeight)
    : null;

  return (
    <div className="space-y-6">
      {/* Message */}
      {message && (
        <div className={`p-3 rounded-lg text-sm ${message.type === 'error' ? 'bg-red-100 text-red-700' : 'bg-green-100 text-green-700'}`}>
          {message.text}
        </div>
      )}

      {/* Stats Card */}
      <div className="bg-gradient-to-br from-purple-50 to-white rounded-xl shadow-sm p-6">
        {currentWeight ? (
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 mb-1">Current Weight</p>
              <div className="flex items-baseline gap-2">
                <span className="text-4xl font-bold text-purple-600">
                  {toDisplay(currentWeight.weightLbs)}
                </span>
                <span className="text-lg text-gray-500">{unit}</span>
                {trend && (
                  <span className={`flex items-center ${getTrendColor()}`}>
                    {trend === 'down' ? <TrendingDown size={18} /> : trend === 'up' ? <TrendingUp size={18} /> : <Minus size={18} />}
                  </span>
                )}
              </div>
              <p className="text-xs text-gray-400 mt-1">{formatDisplayDate(currentWeight.date)}</p>
            </div>

            {goalWeight && (
              <div className="text-right">
                <p className="text-sm text-gray-600 mb-1">Goal</p>
                <div className="text-2xl font-bold text-gray-700">
                  {toDisplay(goalWeight)} <span className="text-sm text-gray-500">{unit}</span>
                </div>
                {weightDiff !== null && (
                  <p className={`text-sm mt-1 ${Math.abs(weightDiff) < 0.5 ? 'text-green-600 font-semibold' : 'text-gray-500'}`}>
                    {Math.abs(weightDiff) < 0.5
                      ? 'Goal reached!'
                      : `${Math.abs(weightDiff).toFixed(1)} ${unit} to go`
                    }
                  </p>
                )}
              </div>
            )}
          </div>
        ) : (
          <div className="text-center py-4">
            <p className="text-gray-500">No weight entries yet</p>
            <p className="text-sm text-gray-400 mt-1">Log your first weight below</p>
          </div>
        )}

        {/* Progress Bar */}
        {goalWeight && weightEntries.length > 0 && progressToGoal !== null && (
          <div className="mt-4">
            <div className="flex justify-between text-xs text-gray-500 mb-1">
              <span>Start: {toDisplay(weightEntries[0].weightLbs)} {unit}</span>
              <span>{progressToGoal}%</span>
            </div>
            <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
              <div
                className={`h-full rounded-full transition-all ${progressToGoal >= 100 ? 'bg-green-500' : 'bg-purple-500'}`}
                style={{ width: `${Math.min(100, progressToGoal)}%` }}
              />
            </div>
          </div>
        )}
      </div>

      {/* Log Weight Form */}
      <div className="bg-white rounded-xl shadow-sm p-4">
        <h3 className="font-semibold text-gray-800 mb-3">Log Weight</h3>
        <div className="flex gap-2">
          <input
            type="date"
            value={dateInput}
            onChange={(e) => setDateInput(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 outline-none text-sm"
          />
          <div className="flex-1 relative">
            <input
              type="number"
              step="0.1"
              value={weightInput}
              onChange={(e) => setWeightInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  logWeight();
                }
              }}
              placeholder="Weight"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 outline-none pr-12"
            />
            <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-gray-400">{unit}</span>
          </div>
          <button
            onClick={logWeight}
            disabled={!weightInput.trim()}
            className="bg-purple-600 text-white px-4 py-2 rounded-lg font-medium hover:bg-purple-700 disabled:opacity-50 transition"
          >
            Log
          </button>
        </div>
      </div>

      {/* Chart */}
      {chartData.length >= 2 && (
        <div className="bg-white rounded-xl shadow-sm p-4">
          <div className="flex justify-end mb-4">
            <div className="flex gap-1">
              {['1w', '1m', '3m', '6m', '1y', 'all'].map(period => (
                <button
                  key={period}
                  onClick={() => setChartPeriod(period)}
                  className={`px-2 py-1 text-xs rounded font-medium transition ${
                    chartPeriod === period
                      ? 'bg-purple-100 text-purple-700'
                      : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
                  }`}
                >
                  {period.toUpperCase()}
                </button>
              ))}
            </div>
          </div>

          <ResponsiveContainer width="100%" height={200}>
            <LineChart data={chartData}>
              <XAxis
                dataKey="label"
                tick={{ fontSize: 11, fill: '#9ca3af' }}
                axisLine={false}
                tickLine={false}
                interval="preserveStartEnd"
              />
              <YAxis
                domain={['dataMin - 2', 'dataMax + 2']}
                tick={{ fontSize: 11, fill: '#9ca3af' }}
                axisLine={false}
                tickLine={false}
                width={45}
              />
              <Tooltip
                contentStyle={{ backgroundColor: '#fff', border: '1px solid #e5e7eb', borderRadius: '8px', fontSize: '12px' }}
                formatter={(value) => [`${value} ${unit}`, 'Weight']}
                labelFormatter={(label) => label}
              />
              {goalWeight && (
                <ReferenceLine
                  y={toDisplay(goalWeight)}
                  stroke="#22c55e"
                  strokeDasharray="5 5"
                  label={{ value: `Goal`, position: 'right', fontSize: 10, fill: '#22c55e' }}
                />
              )}
              {showYearLines && getYearBoundaries(chartData).map(b => (
                <ReferenceLine
                  key={b.year}
                  x={b.label}
                  stroke="#e5e7eb"
                  strokeWidth={1.5}
                  label={{ value: b.year, position: 'insideTopLeft', fontSize: 10, fill: '#9ca3af' }}
                />
              ))}
              <Line
                type="monotone"
                dataKey="weight"
                stroke="#9333ea"
                strokeWidth={2}
                dot={showDots ? { fill: '#9333ea', r: 3 } : false}
                activeDot={{ r: 5 }}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* Recent Entries */}
      {weightEntries.length > 0 && (
        <div className="bg-white rounded-xl shadow-sm p-4">
          <h3 className="font-semibold text-gray-800 mb-3">Recent Entries</h3>
          <div className="space-y-0 max-h-64 overflow-y-auto">
            {[...weightEntries].reverse().map(entry => (
              <div key={entry.id} className="flex items-center justify-between py-2.5 border-b border-gray-100 last:border-0">
                <span className="text-sm text-gray-600">{formatDisplayDate(entry.date)}</span>
                <div className="flex items-center gap-3">
                  <span className="font-semibold text-purple-600">{toDisplay(entry.weightLbs)} {unit}</span>
                  <button
                    onClick={() => deleteEntry(entry.id)}
                    className="text-red-400 hover:text-red-600 transition p-1"
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default WeightTracker;
